import React, { useEffect, useMemo, useRef, useState } from 'react'
import Context from './Context'
import theme from './theme'
import { Backdrop, Box, CircularProgress, ThemeProvider } from '@mui/material'
import Odds from './pages/Odds'
import AppBar from './pages/AppBar'
import SideBar from './pages/SideBar'
import Login from './pages/Login'
import SettingsPage from './pages/SettingsPage'
import NotificationsPage from './pages/NotificationsPage'
import NotificationSettingsPage from './pages/NotificationSettingsPage'
import ArbPage from './pages/ArbPage'
import GamesPage from './pages/GamesPage'
import SupportPage from './pages/SupportPage'
import FavoritesBoardPage from './pages/FavoritesBoardPage'
import { isAuthenticated, isLoginOnlyHost, DEMO_REDIRECT_URL } from './utils/auth'
import { loadSettings, saveSettings, pushSettings, withDefaults } from './utils/settings'
import { apiJson } from './utils/api'
import { useLocation, useNavigate } from 'react-router-dom'
import { stateFromPath, pathForPage, pathForEvent, pathForLeague, sameLeague } from './utils/routes'
import { setAlertsEnabled } from './utils/sound'
import '@fontsource/source-sans-pro'

function App() {
  const [ authenticated, setAuthenticatedState ] = useState(isAuthenticated)
  const loginOnly = isLoginOnlyHost()
  const [ settings, setSettings ] = useState(loadSettings)
  const [ pollingInterval ] = useState(2000) // in ms
  // the grid, the menu and favourites all go through our own server too, it proxies them on to theta,
  // so the browser only ever talks to the host it was served from
  const [ polledHost ] = useState(process.env.REACT_APP_MARKET_HOST || '')
  // the node server in server/index.js: it polls the market snapshot and hands out one event at a time.
  // in production it serves this build too, so the api is same origin; .env.development points at :4000
  const [ marketHost ] = useState(process.env.REACT_APP_MARKET_HOST || '')
  const [ sideBarWidth ] = useState(200)
  const [ selectedLeague, setSelectedLeague ] = useState('')
  const [ selectedBookmaker, setSelectedBookmaker ] = useState('Bovada')
  const [ usPrices, setUsPrices ] = useState(false)
  const [ showDiscrepancies, setShowDiscrepancies ] = useState(false)
  const [ showArbs, setShowArbs ] = useState(false)
  /**
   * Which screen is showing, which league and which event are all read off the address. The setters below
   * navigate rather than assign, so the menu, the browser's own arrows and a pasted link all go through
   * exactly the same path and cannot disagree with each other.
   */
  const location = useLocation()
  const navigate = useNavigate()
  /**
   * Memoised on the path, not recomputed per render. selectedEvent and currentLeague are objects, and a
   * fresh one every render makes every effect that depends on them fire every render — the event screen
   * re-requested itself in a loop and never stopped showing its spinner.
   */
  const route = useMemo(() => stateFromPath(location.pathname), [location.pathname])

  const activePage = route.page
  const showSettings = activePage === 'settings'
  const showNotifications = activePage === 'notifications'
  const showNotificationSettings = activePage === 'notificationSettings'
  const showSearch = activePage === 'search'
  const showSupport = activePage === 'support'
  const showBoard = activePage === 'board'

  /**
   * Only move if this actually changes where we are. Every menu entry clears the open event and the other
   * pages on its way in, and without this each of those clears would push its own history entry — Back
   * would then step through a trail of identical addresses instead of the screen you came from.
   */
  const goTo = (path) => {
    if (path !== location.pathname) {
      navigate(path)
    }
  }

  /**
   * Turning a page off means "if you are on it, leave" — not "go to the front page". Every menu entry
   * calls all three of these on its way in to close whatever else was open, and treating those as
   * navigations dropped the league from the address and left the grid polling with nothing selected.
   */
  const leave = (isOn) => {
    if (isOn) {
      goTo('/')
    }
  }

  const setShowSettings = (on) => (on ? goTo(pathForPage('settings')) : leave(showSettings))
  const setShowNotifications = (on) => (on ? goTo(pathForPage('notifications')) : leave(showNotifications))
  const setShowNotificationSettings = (on) =>
    (on ? goTo(pathForPage('notificationSettings')) : leave(showNotificationSettings))
  const setShowSearch = (on) => (on ? goTo(pathForPage('search')) : leave(showSearch))
  const setShowSupport = (on) => (on ? goTo(pathForPage('support')) : leave(showSupport))
  const setShowBoard = (on) => (on ? goTo(pathForPage('board')) : leave(showBoard))

  const selectedEvent = route.selectedEvent
  const setSelectedEvent = (event) => {
    // clearing an event that is not open is what every menu click asks for, and it means nothing here
    if (!event && !route.selectedEvent) {
      return
    }
    goTo(event ? pathForEvent(event) : '/')
  }
  const [ isSideBarOpen, setIsSideBarOpen ] = useState(true)
  const [ requestCount, setRequestCount ] = useState(0)
  const [ lastResponseTime, setLastResponseTime ] = useState(null)
  const [ isMenuLoading, setIsMenuLoading ] = useState(true)
  const [ isLeagueLoading, setIsLeagueLoading ] = useState(false)
  const [ isFavoriteChanging, setIsFavoriteChanging ] = useState(false)
  const currentLeague = route.currentLeague

  const setCurrentLeague = (league) => {
    if (sameLeague(league, currentLeague)) {
      return
    }
    goTo(pathForLeague(league))
  }

  /**
   * The menu picks a league for itself as soon as it knows what exists. That is not somewhere anybody
   * navigated to, so it only applies on the bare address: arriving on a link to an event or a settings
   * page must not be overruled a second later by the menu deciding where we ought to be. And it replaces
   * the entry rather than adding one, so Back does not land on a blank address that bounces forward.
   */
  const setInitialLeague = (league) => {
    /**
     * Read from the browser, not from the render this function was created in. The menu calls this when
     * its feed request comes back, seconds later, holding whichever copy of `location` existed when the
     * request went out — which is how opening a link could be overruled a moment afterwards by the menu
     * deciding we ought to be looking at the default league.
     */
    if (window.location.pathname !== '/') {
      return
    }
    navigate(pathForLeague(league), { replace: true })
  }
  // filled in by the menu from whatever the feed actually carries: [[sport, [{league, count}]], ...]
  const [ knownLeagues, setKnownLeagues ] = useState([])

  const [ username, setUsername ] = useState('')

  // localStorage paints the first frame, the account behind the token is the real answer a moment later
  useEffect(() => {
    if (!authenticated) {
      return undefined
    }
    let cancelled = false
    apiJson(marketHost, '/api/me')
      .then((me) => {
        if (cancelled) {
          return
        }
        setUsername(me.username)
        if (me.settings) {
          // an account saved before a setting existed has no such key: fill the gaps rather than
          // hand a half object to screens that index straight into it
          const settings = withDefaults(me.settings)
          settingsRef.current = settings
          setSettings(settings)
          saveSettings(settings)
        }
      })
      .catch(() => {})
    return () => { cancelled = true }
  }, [marketHost, authenticated])

  // the sound module is told once, here, and every caller obeys it without knowing about settings
  useEffect(() => {
    setAlertsEnabled(settings.notificationSound !== false)
  }, [settings.notificationSound])

  /**
   * The settings anyone wrote last, readable without waiting for a render. A screen that saves after a
   * pause — the search filters do, so a dragged slider is one write and not thirty — would otherwise send
   * the object it captured before the pause and quietly undo whatever landed in between: the account's
   * own copy arriving from /api/me, or a threshold changed on another screen. Callers that only mean to
   * change their own corner pass a function and are handed the current object to build on.
   */
  const settingsRef = useRef(settings)
  settingsRef.current = settings

  const persistSettings = (next) => {
    const value = typeof next === 'function' ? next(settingsRef.current) : next
    settingsRef.current = value
    setSettings(value)
    saveSettings(value)
    pushSettings(marketHost, value).catch(() => {})
  }

  // on the apex there is nothing behind the form: an authorised visitor is forwarded to the demo subdomain
  if (loginOnly && authenticated && DEMO_REDIRECT_URL) {
    window.location.href = DEMO_REDIRECT_URL
    return null
  }

  if (loginOnly || !authenticated) {
    return (
      <ThemeProvider theme={theme}>
        <Login
          marketHost={marketHost}
          onSuccess={(account) => {
            setUsername(account.username)
            if (account.settings) {
              const settings = withDefaults(account.settings)
              setSettings(settings)
              saveSettings(settings)
            }
            setAuthenticatedState(true)
          }}
        />
      </ThemeProvider>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <Context.Provider value={{
        minYieldValue: settings.minYield,
        settings, setSettings: persistSettings,
        knownLeagues, setKnownLeagues,
        polledHost,
        marketHost,
        showArbs, setShowArbs,
        showSettings, setShowSettings,
        activePage,
        showSearch, setShowSearch,
        showSupport, setShowSupport,
        showBoard, setShowBoard,
        showNotifications, setShowNotifications,
        showNotificationSettings, setShowNotificationSettings,
        username,
        selectedEvent, setSelectedEvent,
        isSideBarOpen, setIsSideBarOpen,
        usPrices, setUsPrices,
        showDiscrepancies, setShowDiscrepancies,
        currentLeague, setCurrentLeague, setInitialLeague,
        requestCount, setRequestCount,
        lastResponseTime, setLastResponseTime,
        isMenuLoading, setIsMenuLoading,
        setIsLeagueLoading,
        selectedBookmaker, setSelectedBookmaker,
        selectedLeague, setSelectedLeague,
        isFavoriteChanging, setIsFavoriteChanging
      }}>
        <Box sx={{ width: '100%', height: '100vh', flexGrow: 1, flexDirection: 'column', alignItems: 'center' }}>
          <SideBar sideBarWidth={sideBarWidth} />
          <Box component="main"
            sx={{
              marginLeft: isSideBarOpen ? `${sideBarWidth}px` : '0px',
              width: isSideBarOpen? `calc(100% - ${sideBarWidth}px)` : '100%',
              transition: 'margin-left 0.3s',
              height: '100%',
              overflowY: 'auto',
              backgroundColor: theme.palette.background.paper
            }}
          >
            <AppBar />
            {showSettings && <SettingsPage />}
            {showNotifications && <NotificationsPage />}
            {showNotificationSettings && <NotificationSettingsPage />}
            {activePage === 'arb' && <ArbPage arbId={route.arbId} pollingInterval={pollingInterval} />}
            {showSearch && <GamesPage pollingInterval={pollingInterval} />}
            {showSupport && <SupportPage />}
            {showBoard && <FavoritesBoardPage pollingInterval={pollingInterval} />}
            {activePage === 'odds' && <Odds pollingInterval={pollingInterval} />}
          </Box>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isMenuLoading || isLeagueLoading }
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Box>
      </Context.Provider>
    </ThemeProvider>
  )
}

export default App
